import { Alert, Button, Card, Form, Input, Radio, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
    DatePicker as ZhDatePicker,
    DateRangePicker as ZhDateRangePicker,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api/request";
import { DATE_PICKER_FORMAT, LOG_DATE_FORMAT } from "@/config";
import { HOW_TO_SIGN } from "@/meta/purchase/contract";

export interface ContractFormValues {
    projectId?: number | string;
    type?: number | string;
    name?: string;
    signMode?: number | string;
    buyerCustomerId?: number | string;
    /** 旧 Vue 表单使用的卖方客户字段名，提交时必须保持该 API 拼写。 */
    sallerCustomerId?: number | string;
    fileId?: number | string;
    completionTime?: string;
    startDate?: string;
    finishDate?: string;
}

export interface ContractPayload {
    projectId: number;
    type: number;
    name: string;
    signMode: number;
    buyerCustomerId: number;
    /** 兼容旧 Vue PurchaseContractForm 的卖方客户字段名。 */
    sallerCustomerId?: number;
    fileId: number;
    completionTime: string;
    startDate: string;
    finishDate: string;
    submitType: number;
}

type ContractDetail = Partial<ContractPayload> & {
    id?: number | string;
    projectName?: string;
    sellerCustomerId?: number | string;
};

export interface ContractFormPageProps {
    contractId?: number | string;
    loadContract?: (id: number | string) => Promise<ContractDetail>;
    createContract?: (payload: ContractPayload) => Promise<unknown>;
    updateContract?: (id: number | string, payload: ContractPayload) => Promise<unknown>;
}

/** 直连 Vue 编辑详情接口，避免加载带 decorators 的旧 service。 */
export async function loadPurchaseContract(id: number | string): Promise<ContractDetail> {
    const { data } = await request.service.get<ContractDetail>(`/purchase/contract/find/${id}`);
    return data;
}

/** 直连 Vue 合同新增接口，保持 POST payload 不变。 */
export async function createPurchaseContract(payload: ContractPayload): Promise<unknown> {
    const { data } = await request.service.post<ContractDetail>("/purchase/contract", payload);
    return data;
}

/** 直连 Vue 合同编辑接口，保持 PUT payload 不变。 */
export async function updatePurchaseContract(id: number | string, payload: ContractPayload): Promise<unknown> {
    const { data } = await request.service.put<ContractDetail>(`/purchase/contract/${id}`, payload);
    return data;
}

function toDateInput(value?: string) {
    return value?.slice(0, 10);
}

function toCompletionTime(value: string) {
    return value.length === 10 ? `${value} 00:00:00` : value;
}

function toPayload(values: ContractFormValues, submitType: number): ContractPayload {
    const sallerCustomerId = Number(values.sallerCustomerId);
    return {
        projectId: Number(values.projectId),
        type: Number(values.type),
        name: String(values.name || "").trim(),
        signMode: Number(values.signMode),
        buyerCustomerId: Number(values.buyerCustomerId),
        ...(Number.isFinite(sallerCustomerId) ? { sallerCustomerId } : {}),
        fileId: Number(values.fileId),
        completionTime: toCompletionTime(values.completionTime || ""),
        startDate: values.startDate || "",
        finishDate: values.finishDate || "",
        submitType
    };
}

const CONTRACT_FILE_MAX_SIZE = 10 * 1024 * 1024;
const CONTRACT_FILE_EXTENSIONS = new Set(["zip", "pdf"]);

/** 对齐旧 Vue SingleUpload 的合同文件扩展名和大小校验。 */
function validateContractFile(file: File): boolean {
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    return CONTRACT_FILE_EXTENSIONS.has(extension) && file.size <= CONTRACT_FILE_MAX_SIZE;
}

/** 采购合同新增/编辑最小闭环：沿用 Vue 字段、校验、payload 和保存返回行为。 */
export function ContractFormPage({
    contractId: contractIdProp,
    loadContract = loadPurchaseContract,
    createContract = createPurchaseContract,
    updateContract = updatePurchaseContract
}: ContractFormPageProps) {
    const params = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [form] = Form.useForm<ContractFormValues>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [successMessage, setSuccessMessage] = useState<string>();
    const [loading, setLoading] = useState(false);
    // 合同列表沿用 Vue 编辑入口的兼容地址 `/contract/add?id=...`，优先识别显式参数和路径参数。
    const contractId = contractIdProp ?? params.id ?? searchParams.get("id") ?? undefined;
    const editMode = contractId !== undefined && contractId !== "";
    const type = Form.useWatch("type", form);
    const signMode = Form.useWatch("signMode", form);
    const [contractDateRange, setContractDateRange] = useState<[string | null, string | null]>([null, null]);
    const [contractFileList, setContractFileList] = useState<UploadUserFile[]>([]);

    useEffect(() => {
        if (!editMode) {
            form.setFieldsValue({ signMode: HOW_TO_SIGN.UNDERLINE.code });
            setContractDateRange([null, null]);
            setContractFileList([]);
            return;
        }

        let disposed = false;
        setLoading(true);
        void loadContract(contractId)
            .then(detail => {
                if (disposed) return;
                const completionTime = toDateInput(detail.completionTime);
                const startDate = toDateInput(detail.startDate);
                const finishDate = toDateInput(detail.finishDate);
                form.setFieldsValue({
                    projectId: detail.projectId,
                    type: detail.type,
                    name: detail.name,
                    signMode: detail.signMode,
                    buyerCustomerId: detail.buyerCustomerId,
                    // 旧 Vue vo2form 将响应中的 sellerCustomerId 映射为 sallerCustomerId 后原样提交。
                    sallerCustomerId: detail.sellerCustomerId,
                    fileId: detail.fileId,
                    completionTime,
                    startDate,
                    finishDate
                });
                // 编辑回显同步可视范围，提交字段仍保持 Vue 的两个日期字段。
                setContractDateRange([startDate || null, finishDate || null]);
                setContractFileList(
                    detail.fileId
                        ? [{ uid: Number(detail.fileId), name: `合同文件 ${detail.fileId}`, status: "success" }]
                        : []
                );
                setLoading(false);
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("合同详情加载失败");
                    setLoading(false);
                }
            });
        return () => {
            disposed = true;
        };
    }, [contractId, editMode, form, loadContract]);

    /** 保存前补充 Vue 日期范围校验，并保持保存/提交 submitType。 */
    const save = async (values: ContractFormValues, submitType: number) => {
        if (values.finishDate && values.startDate && values.finishDate < values.startDate) {
            setErrorMessage("合同结束日期不得早于开始日期");
            return;
        }

        try {
            setErrorMessage(undefined);
            const payload = toPayload(values, submitType);
            if (editMode) await updateContract(contractId, payload);
            else await createContract(payload);
            setSuccessMessage("合同保存成功");
            void navigate(-1);
        } catch {
            setSuccessMessage(undefined);
            setErrorMessage("合同保存失败");
        }
    };

    /** 草稿按钮同样执行 Vue 表单必填校验，再把 submitType 设为 0。 */
    const saveDraft = async () => {
        try {
            await save(await form.validateFields(), 0);
        } catch {
            // Ant Design 已经把字段错误渲染在对应表单项下。
        }
    };

    /** 使用现有文件服务完成 zhihao Upload 的自定义上传，并回填 Vue fileId 字段。 */
    const uploadContractFile = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fileName", file.name);
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
            form.setFieldsValue({ fileId });
            setContractFileList([
                { uid: fileId, name: uploadedFile.name || file.name, status: "success", url: uploadedFile.path }
            ]);
            onSuccess(uploadedFile);
        } catch (error) {
            onError(error instanceof Error ? error : new Error("contract upload failed"));
        }
    };

    if (loading) return <Card loading />;

    return (
        <section aria-label="contract-form-page">
            <Card>
                <Typography.Title level={4}>{editMode ? "编辑采购合同" : "新增采购合同"}</Typography.Title>
                {errorMessage ? <Alert className="mb-4" role="alert" title={errorMessage} type="error" /> : null}
                {successMessage ? <Alert className="mb-4" role="alert" title={successMessage} type="success" /> : null}
                <Form<ContractFormValues>
                    form={form}
                    initialValues={{ signMode: HOW_TO_SIGN.UNDERLINE.code }}
                    layout="vertical"
                    onFinish={values => void save(values, 1)}
                >
                    <Typography.Title level={5}>项目信息</Typography.Title>
                    <Form.Item
                        label="关联项目"
                        name="projectId"
                        rules={[{ required: true, message: "请选择关联项目" }]}
                    >
                        <Input aria-label="关联项目" disabled={editMode} type="number" />
                    </Form.Item>
                    <Typography.Title level={5}>基本信息</Typography.Title>
                    <Form.Item label="签署方式" name="signMode" rules={[{ required: true, message: "请选择签署方式" }]}>
                        <Radio.Group aria-label="签署方式">
                            <Radio value={HOW_TO_SIGN.ONLINE.code}>线上</Radio>
                            <Radio value={HOW_TO_SIGN.UNDERLINE.code}>线下</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label="类型" name="type" rules={[{ required: true, message: "请选择类型" }]}>
                        <Select
                            aria-label="类型"
                            className="w-full"
                            options={[
                                { value: 1, label: "供应商-采购商" },
                                { value: 2, label: "联合购销商-采购商" },
                                { value: 3, label: "供应商-联合购销商" }
                            ]}
                            placeholder="请选择类型"
                            showSearch
                            onSearch={value => {
                                const numericValue = Number(value);
                                if ([1, 2, 3].includes(numericValue)) form.setFieldsValue({ type: numericValue });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label="合同名称" name="name" rules={[{ required: true, message: "请输入合同名称" }]}>
                        <Input aria-label="合同名称" maxLength={32} />
                    </Form.Item>
                    {Number(type) === 2 ? (
                        <Form.Item
                            label="买方"
                            name="buyerCustomerId"
                            rules={[{ required: true, message: "请选择买方" }]}
                        >
                            <Input aria-label="买方" type="number" />
                        </Form.Item>
                    ) : (
                        <Form.Item label="买方" name="buyerCustomerId">
                            <Input aria-label="买方" type="number" />
                        </Form.Item>
                    )}
                    <Form.Item
                        label="签订日期"
                        name="completionTime"
                        rules={[{ required: true, message: "请选择完成日期" }]}
                    >
                        <ZhDatePicker
                            aria-label="签订日期"
                            format={DATE_PICKER_FORMAT}
                            placeholder="请选择完成日期"
                            type="date"
                            valueFormat={LOG_DATE_FORMAT}
                        />
                    </Form.Item>
                    <Form.Item label="起止日期" required>
                        <ZhDateRangePicker
                            aria-label="起止日期"
                            endPlaceholder="结束时间"
                            format={DATE_PICKER_FORMAT}
                            rangeSeparator="-"
                            startPlaceholder="开始时间"
                            type="daterange"
                            value={contractDateRange}
                            valueFormat={DATE_PICKER_FORMAT}
                            onChange={(_value, formattedValue) => {
                                // 保留 Vue 日期范围变更后的两个后端字段，不引入新的表单字段。
                                const nextRange: [string | null, string | null] = [
                                    formattedValue?.[0] || null,
                                    formattedValue?.[1] || null
                                ];
                                setContractDateRange(nextRange);
                                form.setFieldsValue({
                                    startDate: nextRange[0] || undefined,
                                    finishDate: nextRange[1] || undefined
                                });
                            }}
                        />
                    </Form.Item>
                    <Form.Item hidden name="startDate" rules={[{ required: true, message: "请选择起始日期" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name="finishDate" rules={[{ required: true, message: "请选择截止日期" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="合同文件" required>
                        <ZhUpload
                            action=""
                            accept=".zip,.pdf"
                            fileList={contractFileList}
                            limit={1}
                            beforeUpload={validateContractFile}
                            httpRequest={uploadContractFile}
                            onPreview={file => {
                                if (file.url) window.open(file.url, "_blank");
                            }}
                            onRemove={() => {
                                form.setFieldsValue({ fileId: undefined });
                                setContractFileList([]);
                            }}
                        >
                            <Button htmlType="button" type="link">
                                上传合同文件
                            </Button>
                        </ZhUpload>
                    </Form.Item>
                    <Form.Item hidden name="fileId" rules={[{ required: true, message: "请上传合同文件" }]}>
                        <Input aria-label="合同文件" />
                    </Form.Item>
                    <Form.Item hidden name="sallerCustomerId">
                        <Input />
                    </Form.Item>
                    <Space>
                        <Button htmlType="submit" type="primary">
                            保存并提交
                        </Button>
                        {Number(signMode) === HOW_TO_SIGN.ONLINE.code ? (
                            <Button htmlType="button" onClick={() => void saveDraft()}>
                                保存为草稿
                            </Button>
                        ) : null}
                        <Button onClick={() => void navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}

export default ContractFormPage;
